/**
 * The `documento` — the portal's identity key.
 *
 * ⚠️ THIS IS THE ONLY IMPLEMENTATION OF THESE RULES IN THE REPOSITORY.
 *    Both `apps/web` and `apps/bff` import from here. Do not reimplement any of
 *    it at a call site, not even for a log line.
 *
 * Why it matters: the login username, the initial password, the GoTrue
 * synthetic e-mail address and every upstream API call are all derived from
 * this value. Two implementations means two answers means two accounts for one
 * person, with their service history split in half — which the customer reports
 * as "the site lost my orders", and which is very hard to diagnose from that.
 *
 * See: ADR-0002, .claude/skills/documento-identity-model/SKILL.md
 */

/** Maximum length of a canonical documento. Contract-enforced. */
export const DOCUMENTO_MAX_LENGTH = 32;

/** Length of the activation credential derived from a documento (ADR-0002). */
export const ACTIVATION_CREDENTIAL_LENGTH = 6;

/** The non-resolving, non-deliverable domain used for GoTrue identities. */
export const PORTAL_EMAIL_DOMAIN = 'portal.newshoes.local';

/**
 * Normalize a documento to its canonical form.
 *
 *   1. remove every character that is not [A-Za-z0-9]
 *   2. uppercase the remaining ASCII letters
 *
 *   "123.456.789-09"  ->  "12345678909"
 *   "ab 123 456"      ->  "AB123456"
 *
 * The uppercase step extends the kickoff brief's rule, which is unambiguous for
 * digits and ambiguous the moment a document contains letters — and the brief
 * explicitly says not to restrict to CPF. Without a case rule, `ab123456` and
 * `AB123456` become two accounts with two different passwords, which is exactly
 * the failure normalization exists to prevent. See open question 1.
 */
export function normalizeDocumento(raw: string): string {
  return raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/** True when a value is already in canonical form and within the length bounds. */
export function isCanonicalDocumento(value: string): boolean {
  return /^[A-Z0-9]{1,32}$/.test(value);
}

export type DocumentoValidation =
  | { ok: true; canonical: string }
  | { ok: false; reason: 'empty' | 'too_long' | 'too_short_for_credential' };

/**
 * Normalize and validate a documento for **provisioning**.
 *
 * A canonical documento shorter than {@link ACTIVATION_CREDENTIAL_LENGTH} is
 * rejected rather than silently given a shorter password. Working assumption
 * from open question 1 — which document types actually occur, and what the
 * shortest one is, are not yet known.
 */
export function validateForProvisioning(raw: string): DocumentoValidation {
  const canonical = normalizeDocumento(raw);
  if (canonical.length === 0) return { ok: false, reason: 'empty' };
  if (canonical.length > DOCUMENTO_MAX_LENGTH) return { ok: false, reason: 'too_long' };
  if (canonical.length < ACTIVATION_CREDENTIAL_LENGTH) {
    return { ok: false, reason: 'too_short_for_credential' };
  }
  return { ok: true, canonical };
}

/**
 * Derive the initial activation credential: the first 6 characters of the
 * **canonical** documento.
 *
 * ⚠️ This is an ACTIVATION CREDENTIAL, NOT A PASSWORD. It is a public function
 *    of a value (a CPF) that is not secret in Brazil, so it is only safe behind
 *    the forced-rotation gate, the 72-hour expiry, the anti-derivation rule,
 *    lockout, the no-enumeration rule and the audit trail. Never treat it as a
 *    password, and never weaken any of those controls. See ADR-0003.
 *
 * Throws if the documento is too short — callers should use
 * {@link validateForProvisioning} first.
 */
export function deriveActivationCredential(rawOrCanonical: string): string {
  const canonical = normalizeDocumento(rawOrCanonical);
  if (canonical.length < ACTIVATION_CREDENTIAL_LENGTH) {
    throw new Error(
      `documento too short to derive an activation credential ` +
        `(${canonical.length} < ${ACTIVATION_CREDENTIAL_LENGTH})`,
    );
  }
  return canonical.slice(0, ACTIVATION_CREDENTIAL_LENGTH);
}

/**
 * The GoTrue identity for a documento.
 *
 * GoTrue is e-mail-centric, so the documento is mapped onto a synthetic address
 * on a deliberately non-resolving, non-deliverable domain: no mail can ever be
 * sent to it by accident, and it cannot be confused with a real customer
 * address. The local part is lowercased because GoTrue lowercases e-mail; the
 * authoritative documento lives in the user's metadata. (ADR-0002)
 *
 * ⚠️ Always build the address with this function. Never concatenate it at a
 *    call site — that is how a lookup silently misses an account.
 */
export function documentoToPortalEmail(rawOrCanonical: string): string {
  const canonical = normalizeDocumento(rawOrCanonical);
  if (canonical.length === 0) throw new Error('cannot build a portal e-mail from an empty documento');
  return `${canonical.toLowerCase()}@${PORTAL_EMAIL_DOMAIN}`;
}

/**
 * Whether a candidate password is derivable from the documento, and must
 * therefore be rejected at rotation (ADR-0003).
 *
 * Rejects the documento itself, **any substring of it** — which covers "the
 * first 6", "the last 4" and every other slice a customer might reach for — and
 * any punctuated form of it. Rejecting a substring of a *public* value is not
 * password-strength theatre; it closes the exact hole the activation gate
 * exists for.
 */
export function isDerivableFromDocumento(candidate: string, documento: string): boolean {
  const canonicalDoc = normalizeDocumento(documento);
  if (canonicalDoc.length === 0) return false;

  // Compare the candidate both as typed and normalized, so "123.456" is caught
  // as readily as "123456".
  for (const form of [candidate, normalizeDocumento(candidate)]) {
    if (form.length === 0) continue;
    if (canonicalDoc.includes(form.toUpperCase())) return true;
    if (form.toUpperCase() === canonicalDoc) return true;
  }
  return false;
}
